
import { useEffect } from "react";

import { 
    ActionStatusHelper, 
    ExportAction, 
    FeedBackType, 
    LONG_PROCESS_TOAST_DURATION, 
    MessageHelper, 
    ResponseHook, 
    UIFeedbackHooks, 
    ExportParams,
    EXPORT_FORMATS,
    DomainType,
    DOMAIN_CONFIGS,
    openOrDownloadFile
} from "@/Shared";
import { ExportFormat } from "@/Domains";


export interface UseExportDomainOptions<T> {
    /** Domain label used in messages (e.g. "User", "Contact") */
    domainType: DomainType;

    /** Whether the current user is allowed to export */
    canExport: boolean;

    exportAction : ExportAction<T>;

    /** UI feedback hooks (toast, confirm) */
    uiFeedbackHooks: UIFeedbackHooks;

    /** Callback executed after a successful export */
    onSuccess?: () => void;
}


/**
 * useExportDomain hook
 *
 * Generic domain-level hook responsible for handling
 * export workflows in a consistent and reusable way.
 *
 * It orchestrates:
 * - permission checks
 * - confirmation dialog
 * - async export execution
 * - toast feedback (loading / success / error)
 *
 * This hook is UI-agnostic and domain-agnostic.
 * It relies only on injected callbacks and responses.
 *
 * Author: Samano CASTRE
 * Date: July 2025
 */
export function useExportDomain<T>(options: UseExportDomainOptions<T>) {
    const {
        domainType,
        canExport,
        exportAction,
        uiFeedbackHooks,
        onSuccess,
    } = options;

    const {domainLabel} = DOMAIN_CONFIGS[domainType];
    const { toastHook, confirmHook } = uiFeedbackHooks;
    const {execute, response, setResponse} = exportAction;

    /**
     * Watches the export async response and triggers
     * the appropriate UI feedback.
     */
    useEffect(() => {
        const { isSucceeded, isFailed } = ActionStatusHelper.is(
            response.status
        );

        if (isSucceeded) {
            toastHook.openToast?.({
                data: {
                    message: MessageHelper.EXPORTING.SUCCESS(domainLabel),
                    type: FeedBackType.SUCCESS,
                },
                onClose: () => {
                    ResponseHook.reset(setResponse);
                    onSuccess?.();
                }
            });
        }

        if (isFailed) {
            toastHook.openToast?.({
                data: {
                    message:
                        response.error ??
                        MessageHelper.EXPORTING.ERROR(domainLabel),
                    type: FeedBackType.ERROR,
                },
                onClose: () => ResponseHook.reset(setResponse)
            });
        }
    }, [response.status, response.error]);

    /**
     * Executes the export workflow:
     * 1. Permission check
     * 2. User confirmation
     * 3. Loading feedback
     * 4. Export API call
     */
    const executeExport = async (params : ExportParams<any>, format : ExportFormat) => {
        if (!canExport) {
            toastHook.openToast?.({
                data: {
                    message: MessageHelper.EXPORTING.NOT_AUTHORIZED(domainLabel),
                    type: FeedBackType.WARNING,
                }
            });
            return;
        }

        let confirmMessage = MessageHelper.MODAL.CONFIRM.EXPORTING.DEFAULT(domainLabel);
        
        switch (format) {
            case EXPORT_FORMATS.EXCEL:
                confirmMessage = MessageHelper.MODAL.CONFIRM.EXPORTING.EXCEL(domainLabel);
                break;
            case EXPORT_FORMATS.PDF:
                confirmMessage = MessageHelper.MODAL.CONFIRM.EXPORTING.PDF(domainLabel);
                break;
            default:
                console.warn("Format d'export inconnu");
                break;
        }

        const confirmed = await confirmHook.openConfirm?.({
            title: MessageHelper.MODAL.CONFIRM.TITLE,
            content: confirmMessage,
        });

        if (!confirmed || !execute) {
            return;
        }

        toastHook.openToast?.({
            data: {
                message: MessageHelper.EXPORTING.LOADING(domainLabel),
                type: FeedBackType.INFO,
                duration: LONG_PROCESS_TOAST_DURATION
            }
        });

        const data = await execute(params, format);
        if(data) {
            let extension = '.txt';
            switch (format) {
                case EXPORT_FORMATS.EXCEL:
                    extension = 'xlsx';
                    break;
                case EXPORT_FORMATS.CSV:
                case EXPORT_FORMATS.PDF:
                    extension = format;
                    break;
                default:
                    extension = 'txt';
                    break;
            }
            openOrDownloadFile(
                data.blob, 
                data.filename ?? domainType + "-" + new Date().toISOString() + "." + extension
            );
        }
    };

    /**
     * Public API
     */
    return {
        executeExport,
    };
}